import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { MemberPosition, MemberStatus } from "@satyrsmc/shared/lib/enums";
import { ALL_MEMBERS_ID } from "@satyrsmc/shared/lib/constants";
import type { Event } from "@satyrsmc/shared/client";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ui/confirm-dialog";

interface EmailAssignmentsDialogProps {
  event: Event;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmailAssignmentsDialog({ event, open, onOpenChange }: EmailAssignmentsDialogProps) {
  const [fromMemberId, setFromMemberId] = useState<string | undefined>(undefined);
  const { confirm, confirmDialog } = useConfirm();

  const { data: members } = trpc.admin.members.list.useQuery(undefined, { enabled: open });

  const activeMembers = useMemo(() => {
    return (members ?? [])
      .filter((m) => m.status === MemberStatus.Active && m.email)
      .map((m) => ({ id: m.id, name: m.name, position: m.position ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  const correspondenceSecretaryId = useMemo(
    () => activeMembers.find((m) => m.position === MemberPosition.CorrespondenceSecretary)?.id,
    [activeMembers],
  );
  const effectiveFromMemberId = fromMemberId ?? correspondenceSecretaryId;

  // Distinct assigned members across every role, excluding the ALL_MEMBERS_ID
  // sentinel (roles marked "applies to all members" — not a real mailbox).
  const recipientCount = useMemo(() => {
    const ids = new Set<string>();
    for (const assignment of event.assignments ?? []) {
      for (const am of assignment.members ?? []) {
        if (am.member_id === ALL_MEMBERS_ID) continue;
        ids.add(am.member_id);
      }
    }
    return ids.size;
  }, [event.assignments]);

  const emailAssignments = trpc.admin.events.emailAssignments.useMutation({
    onSuccess: ({ sent, skipped }) => {
      const base = `Emailed ${sent} member${sent === 1 ? "" : "s"} their assignments`;
      toast.success(skipped > 0 ? `${base} · ${skipped} skipped (no email)` : base);
      handleOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  function handleOpenChange(next: boolean) {
    if (!next) setFromMemberId(undefined);
    onOpenChange(next);
  }

  const canSend = !emailAssignments.isPending && !!effectiveFromMemberId && recipientCount > 0;

  async function handleSend() {
    if (!effectiveFromMemberId) return;
    const ok = await confirm(
      `Send assignment emails to ${recipientCount} member${recipientCount === 1 ? "" : "s"}?`,
    );
    if (!ok) return;
    emailAssignments.mutate({ event_id: event.id, from_member_id: effectiveFromMemberId });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email Assignments</DialogTitle>
            <DialogDescription>
              Sends each assigned member their own list of roles for this event. Members without an
              email on file are skipped.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">From</p>
              <Select value={effectiveFromMemberId} onValueChange={setFromMemberId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a sender…" />
                </SelectTrigger>
                <SelectContent>
                  {activeMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground">
              This will email {recipientCount} assigned member{recipientCount === 1 ? "" : "s"}.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
              {emailAssignments.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Mail className="size-4" />
              )}
              {emailAssignments.isPending
                ? "Sending…"
                : `Send${recipientCount ? ` to ${recipientCount}` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </>
  );
}
